import {useEffect, useRef} from 'react';

export interface StopAutoplayOnManualScrollOptions {
  disabled?: boolean;
}

const MANUAL_SCROLL_KEYS = new Set([
  'ArrowLeft',
  'ArrowRight',
  'Home',
  'End',
  'PageUp',
  'PageDown',
]);

const GESTURE_DIRECTION_THRESHOLD_PX = 8;

const MANUAL_INTENT_TIMEOUT_MS = 300;

/**
 * Calls `onManualScroll` when the user scrolls the element horizontally themselves.
 *
 * A `scroll` event alone cannot be attributed, because an autoplay that scrolls the
 * element programmatically fires the very same event. This hook therefore treats the
 * `scroll` event as the proof that something scrolled, and a preceding horizontal
 * input (touch/pointer drag, horizontal wheel, scroll key) as the proof that the user
 * caused it. Scrolling vertically while hovering the element is not a horizontal input,
 * so it never triggers the callback.
 *
 * The callback is invoked at most once, all listeners are removed afterwards.
 */
export const useStopAutoplayOnManualScroll = (
  elementRef: React.RefObject<HTMLElement | null>,
  onManualScroll: VoidFunction,
  {disabled = false}: StopAutoplayOnManualScrollOptions = {},
) => {
  const onManualScrollRef = useRef(onManualScroll);

  useEffect(() => {
    onManualScrollRef.current = onManualScroll;
  }, [onManualScroll]);

  useEffect(() => {
    const element = elementRef.current;

    if (!element || disabled) {
      return;
    }

    const cleanUps: VoidFunction[] = [];

    const listen = <Type extends keyof HTMLElementEventMap>(
      type: Type,
      listener: (event: HTMLElementEventMap[Type]) => void,
      options?: AddEventListenerOptions,
    ) => {
      element.addEventListener(type, listener, options);
      cleanUps.push(() => element.removeEventListener(type, listener, options));
    };

    const stopListening = () => {
      cleanUps.forEach(cleanUp => cleanUp());
      cleanUps.length = 0;
    };

    let gestureStart: {x: number; y: number} | undefined;
    let isHorizontalGesture = false;
    let manualIntentAt: number | undefined;

    const markManualIntent = () => {
      manualIntentAt = Date.now();
    };

    const startGesture = (x: number, y: number) => {
      gestureStart = {x, y};
      isHorizontalGesture = false;
    };

    const moveGesture = (x: number, y: number) => {
      if (isHorizontalGesture) {
        markManualIntent();
        return;
      }

      if (!gestureStart) {
        return;
      }

      const deltaX = x - gestureStart.x;
      const deltaY = y - gestureStart.y;

      if (
        Math.abs(deltaX) < GESTURE_DIRECTION_THRESHOLD_PX &&
        Math.abs(deltaY) < GESTURE_DIRECTION_THRESHOLD_PX
      ) {
        return;
      }

      isHorizontalGesture = Math.abs(deltaX) > Math.abs(deltaY);

      if (isHorizontalGesture) {
        markManualIntent();
      } else {
        gestureStart = undefined;
      }
    };

    const endGesture = () => {
      if (isHorizontalGesture) {
        markManualIntent();
      }

      gestureStart = undefined;
      isHorizontalGesture = false;
    };

    listen(
      'touchstart',
      event => {
        const touch = event.touches[0];
        if (touch) {
          startGesture(touch.clientX, touch.clientY);
        }
      },
      {passive: true},
    );

    listen(
      'touchmove',
      event => {
        const touch = event.touches[0];
        if (touch) {
          moveGesture(touch.clientX, touch.clientY);
        }
      },
      {passive: true},
    );

    listen('touchend', endGesture, {passive: true});
    listen('touchcancel', endGesture, {passive: true});

    // Touch gestures are tracked via touch events, because the browser cancels the
    // pointer as soon as it takes over the panning, which can happen before the
    // pointer moved far enough to give away its direction.
    listen('pointerdown', event => {
      if (event.pointerType !== 'touch') {
        startGesture(event.clientX, event.clientY);
      }
    });

    listen('pointermove', event => {
      if (event.pointerType !== 'touch') {
        moveGesture(event.clientX, event.clientY);
      }
    });

    listen('pointerup', endGesture);
    listen('pointercancel', endGesture);

    listen(
      'wheel',
      event => {
        // Browsers report a shift-wheel either as a horizontal delta or as a vertical
        // one with the modifier set, while both scroll the element horizontally.
        const isHorizontalWheel =
          Math.abs(event.deltaX) > Math.abs(event.deltaY) || event.shiftKey;

        if (isHorizontalWheel) {
          markManualIntent();
        }
      },
      {passive: true},
    );

    listen('keydown', event => {
      if (MANUAL_SCROLL_KEYS.has(event.key)) {
        markManualIntent();
      }
    });

    listen(
      'scroll',
      () => {
        if (
          manualIntentAt === undefined ||
          Date.now() - manualIntentAt > MANUAL_INTENT_TIMEOUT_MS
        ) {
          return;
        }

        stopListening();
        onManualScrollRef.current();
      },
      {passive: true},
    );

    return stopListening;
  }, [elementRef, disabled]);
};
